import { Component, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '../../services/user.service';
import { ValidationLinkService } from '../../services/validation-link.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, map, shareReplay } from 'rxjs';
import { getUrlPathWithSuffix, pathNames, variablePathNames } from '../../utilities/routing-path-utils';
import { errorMessages } from '../../utilities/validator-error-messages-util';
import { ResetPasswordRequest } from '../../models/requests/reset-password-request';
import { mustMatch } from '../../validators/must-match-validator';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgClass, NgIf } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-lost-password-page',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    NgClass,
    NgIf,
    MatProgressSpinnerModule
  ],
  templateUrl: './lost-password-page.component.html',
  styleUrl: './lost-password-page.component.scss'
})
export class LostPasswordPageComponent {
  emailForm!: FormGroup;
  passwordFormGroup!: FormGroup;
  isSendingEmailStep: boolean = false;
  shouldRenderEmailSentCompletion: boolean = false;
  isLoading: boolean = false;
  linkUuid!: string;
  durationInSeconds: number = 5;
  loginPath: string = getUrlPathWithSuffix(pathNames.loginPath);
  private breakpointObserver = inject(BreakpointObserver);

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private userService: UserService, 
    private route: ActivatedRoute, 
    private validationLinkService: ValidationLinkService, 
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.handleInit();
  }

  // SNACKBAR

  openSnackbar(message: string) {
    this.snackBar.open(message, 'Close', { duration: this.durationInSeconds * 1000 });
  }

  // FORMS

  initEmailForm() {
    this.emailForm = this.formBuilder.group({
      email: ['', [Validators.email, Validators.required]]
    });
  }

  initPasswordForm() {
    this.passwordFormGroup = this.formBuilder.group({
      password: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(60)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(60)]]
    });

    this.passwordFormGroup.setValidators(mustMatch('password', 'confirmPassword'));
  }

  submitEmailForm() {
    if (this.emailForm.valid) {
      this.isLoading = true;
      this.createCustomerValidationLinkForPasswordThenSendEmail(this.emailForm.value['email']);
    }
  }

  submitPasswordForm() {
    if (this.passwordFormGroup.valid) {
      this.isLoading = true;
      this.updateUserLostPassword();
    }
  }

  getEmailForm(): { [key: string]: AbstractControl<any, any> } {
    return this.emailForm.controls;
  }

  getPasswordForm() {
    return this.passwordFormGroup.controls;
  }

  // SERVICES

  handleInit() {
    this.route.paramMap.subscribe(params => {
      this.linkUuid = params.get(variablePathNames.lostPasswordPathVariable) || "";
    });
    
    if (this.linkUuid) {
      this.initPasswordForm();
    } else {
      this.isSendingEmailStep = true;
      this.initEmailForm();
    }
  }

  createCustomerValidationLinkForPasswordThenSendEmail(email: string) {
    this.validationLinkService.sendResetPasswordLink(email).subscribe({
      next: this.handleEmailSent,
      error: this.handleEmailSent
    });
  }

  updateUserLostPassword() {
    const request: ResetPasswordRequest = {
      linkId: this.linkUuid,
      password: this.passwordFormGroup.value['password']
    }

    this.userService.resetPassword(request).subscribe({
      next: () => {
        this.isLoading = false;
        this.openSnackbar('The password has been modified with success');
        this.router.navigate([getUrlPathWithSuffix(pathNames.loginPath)]);
      },
      error: error => {
        if (error.error.message.includes(errorMessages.linkNotFound)) {
          this.isLoading = false;
          alert('The link entered is invalid');
          this.router.navigate([getUrlPathWithSuffix(pathNames.notFoundPath)]);
        }
      }
    });
  }

  handleEmailSent = () => {
    this.isSendingEmailStep = false;
    this.shouldRenderEmailSentCompletion = true;
    this.isLoading = false;
  }

  onBack(){
    this.router.navigate(['/' + pathNames.loginPath]);
  }
}